import { FileUp, TriangleAlert } from "lucide-react";
import { type DragEvent, Suspense, useState } from "react";
import { useForm, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { DetailLoading } from "~/components/loading";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "~/components/ui";
import { config } from "~/config";
import { DataImportProjectsModelProvider, useDataImportProjectsModel } from "~/features/dataImportProjects/context";
import { usePrepareEventNormalizationImport } from "~/features/dataImportProjects/hooks/usePrepareEventNormalizationImport";
import { validateEventNormalizationImportFile } from "~/features/dataImportProjects/utils/eventNormalizationImportFile";
import { cn } from "~/lib/utils";
import { v2Route } from "~/v2/routes";

const FORM_ID = "event-normalization-import-form";
const MAX_FILE_SIZE_BYTES = config.eventNormalizationImport.maxFileSizeBytes;

interface EventNormalizationImportFormData {
  name: string;
  description: string;
  referenceId: string;
}

interface EventNormalizationImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EventNormalizationImportDialog({ open, onOpenChange }: EventNormalizationImportDialogProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const form = useForm<EventNormalizationImportFormData>({
    defaultValues: { name: "", description: "", referenceId: "" },
  });
  const [file, setFile] = useState<File | null>(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const { prepare, processing, error, clearError } = usePrepareEventNormalizationImport();
  const fileValidation = file ? validateEventNormalizationImportFile(file, MAX_FILE_SIZE_BYTES) : null;

  const reset = () => {
    form.reset();
    setFile(null);
    setFileInputKey((key) => key + 1);
    clearError();
  };

  const handleOpenChange = (next: boolean) => {
    if (!next && processing) return;
    if (!next) reset();
    onOpenChange(next);
  };

  const selectFile = (nextFile: File) => {
    setFile(nextFile);
    clearError();
  };

  const submit = form.handleSubmit(async (values) => {
    if (!file || !fileValidation?.valid) return;

    const projectId = await prepare({ ...values, file });
    if (!projectId) return;

    reset();
    onOpenChange(false);
    navigate(v2Route.dataImportProjects.detail.$buildPath({ params: { id: projectId } }));
  });

  const validationError =
    fileValidation && !fileValidation.valid
      ? fileValidation.reason === "missingExtension"
        ? t("feature.dataImportProjects.eventNormalizationImport.validation.missingExtension")
        : fileValidation.reason === "unsupportedExtension"
          ? t("feature.dataImportProjects.eventNormalizationImport.validation.unsupportedExtension", {
              extension: fileValidation.extension,
            })
          : t("feature.dataImportProjects.eventNormalizationImport.validation.fileTooLarge", {
              maxFileSize: formatFileSize(MAX_FILE_SIZE_BYTES),
              maxFileSizeBytes: MAX_FILE_SIZE_BYTES.toLocaleString(),
            })
      : null;
  const progressText = processing
    ? t("feature.dataImportProjects.eventNormalizationImport.progress.uploading")
    : error
      ? t("feature.dataImportProjects.eventNormalizationImport.progress.failed")
      : fileValidation?.valid
        ? t("feature.dataImportProjects.eventNormalizationImport.progress.ready")
        : t("feature.dataImportProjects.eventNormalizationImport.progress.notStarted");

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("feature.dataImportProjects.eventNormalizationImport.title")}</DialogTitle>
        </DialogHeader>

        <p className="text-14-regular text-fg-2">
          {t("feature.dataImportProjects.eventNormalizationImport.description")}
        </p>

        <Form {...form}>
          <form id={FORM_ID} className="space-y-4" onSubmit={submit}>
            <FormField
              control={form.control}
              name="name"
              rules={{ validate: (value) => value.trim().length > 0 || t("ui.required") }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("model.common.name")}</FormLabel>
                  <FormControl>
                    <Input {...field} required disabled={processing} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("model.common.description")}</FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={3} disabled={processing} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Suspense fallback={<DetailLoading className="py-3" />}>
              {open && (
                <DataImportProjectsModelProvider>
                  <ReferenceField disabled={processing} />
                </DataImportProjectsModelProvider>
              )}
            </Suspense>

            <div className="space-y-1.5">
              <label className="text-12-medium text-fg-2" htmlFor="event-normalization-import-file">
                {t("feature.dataImportProjects.eventNormalizationImport.file.label")}
              </label>
              <label
                className={cn(
                  "flex flex-col items-center justify-center gap-1.5 rounded-[var(--radius-md)] border border-dashed border-border-default bg-bg-subtle px-6 py-7 text-center transition-colors",
                  processing
                    ? "cursor-not-allowed opacity-60"
                    : "cursor-pointer hover:border-primary-400 hover:bg-primary-50",
                )}
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event: DragEvent<HTMLLabelElement>) => {
                  event.preventDefault();
                  if (processing) return;
                  const droppedFile = event.dataTransfer.files[0];
                  if (droppedFile) selectFile(droppedFile);
                }}
              >
                <FileUp className="h-5 w-5 text-fg-3" />
                <span className="text-14-regular text-fg-2">
                  {t("feature.dataImportProjects.eventNormalizationImport.file.select")}
                </span>
                <span className="text-12-regular text-fg-3">{t("ui.acceptedFileTypes", { accept: ".csv, .tsv" })}</span>
                <span className="text-12-regular text-fg-3">
                  {t("feature.dataImportProjects.eventNormalizationImport.file.maxSize", {
                    maxFileSize: formatFileSize(MAX_FILE_SIZE_BYTES),
                    maxFileSizeBytes: MAX_FILE_SIZE_BYTES.toLocaleString(),
                  })}
                </span>
                <input
                  key={fileInputKey}
                  id="event-normalization-import-file"
                  type="file"
                  accept=".csv,.tsv"
                  disabled={processing}
                  className="sr-only"
                  onChange={(event) => {
                    const selectedFile = event.target.files?.[0];
                    if (selectedFile) selectFile(selectedFile);
                  }}
                />
              </label>
            </div>

            {file && (
              <div className="space-y-3 rounded-[var(--radius-md)] border border-border-default bg-bg-surface p-4">
                <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-14-regular">
                  <dt className="text-fg-3">{t("feature.dataImportProjects.eventNormalizationImport.file.name")}</dt>
                  <dd className="min-w-0 break-all text-fg-1">{file.name}</dd>
                  <dt className="text-fg-3">
                    {t("feature.dataImportProjects.eventNormalizationImport.file.extension")}
                  </dt>
                  <dd className="text-fg-1">
                    {fileValidation?.extension ?? t("feature.dataImportProjects.eventNormalizationImport.file.none")}
                  </dd>
                  <dt className="text-fg-3">{t("feature.dataImportProjects.eventNormalizationImport.file.size")}</dt>
                  <dd className="text-fg-1">{formatFileSize(file.size)}</dd>
                  <dt className="text-fg-3">
                    {t("feature.dataImportProjects.eventNormalizationImport.validation.label")}
                  </dt>
                  <dd className={fileValidation?.valid ? "text-success" : "text-danger"}>
                    {fileValidation?.valid
                      ? t("feature.dataImportProjects.eventNormalizationImport.validation.valid")
                      : t("feature.dataImportProjects.eventNormalizationImport.validation.invalid")}
                  </dd>
                  <dt className="text-fg-3">
                    {t("feature.dataImportProjects.eventNormalizationImport.progress.label")}
                  </dt>
                  <dd className="text-fg-1" aria-live="polite">
                    {progressText}
                  </dd>
                </dl>
                {processing && (
                  <div
                    role="progressbar"
                    aria-label={t("feature.dataImportProjects.eventNormalizationImport.progress.label")}
                    className="h-1.5 w-full overflow-hidden rounded-full bg-bg-subtle"
                  >
                    <div className="h-full w-full animate-pulse rounded-full bg-primary-500" />
                  </div>
                )}
              </div>
            )}

            {(validationError || error) && (
              <div
                role="alert"
                className="flex items-start gap-2 whitespace-pre-wrap rounded-md border border-danger/30 bg-danger/5 px-3.5 py-2.5 text-14-regular text-danger"
              >
                <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{validationError ?? error}</span>
              </div>
            )}
          </form>
        </Form>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={processing}>
            {t("ui.cancel")}
          </Button>
          <Button type="submit" form={FORM_ID} disabled={processing || !fileValidation?.valid}>
            {processing
              ? t("feature.dataImportProjects.eventNormalizationImport.progress.uploading")
              : t("feature.dataImportProjects.eventNormalizationImport.prepare")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReferenceField({ disabled }: { disabled: boolean }) {
  const { t } = useTranslation();
  const { references } = useDataImportProjectsModel();
  const { control } = useFormContext<EventNormalizationImportFormData>();

  return (
    <FormField
      control={control}
      name="referenceId"
      rules={{ required: t("ui.required") }}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{t("model.reference.modelName")}</FormLabel>
          <Select value={field.value} onValueChange={field.onChange} disabled={disabled} required>
            <FormControl>
              <SelectTrigger>
                <SelectValue placeholder={t("model.reference.modelName")} />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {references.map((reference) => (
                <SelectItem key={reference.id} value={reference.id}>
                  {reference.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FormDescription>
            {t("feature.dataImportProjects.eventNormalizationImport.referenceDescription")}
          </FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1_000) return `${bytes.toLocaleString()} B`;
  if (bytes < 1_000_000) return `${(bytes / 1_000).toLocaleString(undefined, { maximumFractionDigits: 1 })} KB`;
  return `${(bytes / 1_000_000).toLocaleString(undefined, { maximumFractionDigits: 1 })} MB`;
}
